import { UseMutationOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "types/models.ts";

import { payoutApi } from "./api";

import GetPayoutResponse = App.Http.Responses.Payout.GetPayoutResponse;
import GetPayoutsRequest = App.Http.Requests.Payout.GetPayoutsRequest;
import GetPayoutsResponse = App.Http.Responses.Payout.GetPayoutsResponse;
import GetPayoutsPairsResponse = App.Http.Responses.Payout.GetPayoutsPairsResponse;
import UpdatePayoutRequest = App.Http.Requests.Payout.UpdatePayoutRequest;
import ExportResponse = App.Http.Responses.ExportResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

// Query to get a list of payouts
export const useGetPayoutsQuery = (request: GetPayoutsRequest, options = {}) => {
    return useQueryApi<GetPayoutsResponse>({
        queryKey: ["getPayouts", request],
        queryFn: () => payoutApi.index(request),
        ...options,
    });
};

// Query to get a list of payout pairs
export const useGetPayoutsPairsQuery = (request: GetPayoutsRequest, options = {}) => {
    return useQueryApi<GetPayoutsPairsResponse>({
        queryKey: ["getPayoutsPairs", request],
        queryFn: () => payoutApi.pairs(request),
        ...options,
    });
};

// Query to get a specific payout
export const useGetPayoutQuery = (payoutId: string, request?: unknown, options = {}) => {
    return useQueryApi<GetPayoutResponse>({
        queryKey: ["getPayout", payoutId],
        queryFn: () => payoutApi.show(payoutId, request),
        ...options,
    });
};

// Mutation to update an existing payout
export const useUpdatePayoutMutation = (
    options: UseMutationOptions<
        GetPayoutResponse,
        AxiosError<ApiErrorResponse>,
        { payoutId: string; request: UpdatePayoutRequest }
    > = {}
) => {
    return useMutationApi<GetPayoutResponse, { payoutId: string; request: UpdatePayoutRequest }>({
        mutationKey: ["updatePayout"],
        mutationFn: ({ payoutId, request }) => payoutApi.update(payoutId, request),
        ...options,
    });
};

export const useGetPayoutsExportMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["getPayoutsExport"],
        mutationFn: (request) => payoutApi.export(request),
        ...options,
    });
};

export const useGetPayoutsPairsExportMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["getPayoutsPairsExport"],
        mutationFn: (request) => payoutApi.exportPairs(request),
        ...options,
    });
};
